import { makeObservable, observable } from "mobx";

import { ObservableModel } from "@use/models";

/**
 * Enumeration of block types used when storing block data in the remote backend
 * so we can easily serialize and deserialize into the proper constructs.
 */
export enum BlockTypes {
    EMBED = "Embed",
    LINK = "Link",
    REFERENCE = "Reference",
    RICHTEXT = "RichText",
    YOUTUBE = "YouTube",
}

/**
 * Specification of raw data needed to create a new block inside a course.
 */
export type BlockData = {
    id: string;
    note: {
        content: string;
        enabled: boolean;
    };
    title: string;
};

/**
 * Specification of the data format used to store blocks in the remote UserData
 * backend.
 */
export type BlockRemoteData = {
    id: string;
    title: string;
    type: string; // tslint:disable-line no-reserved-keywords
    note: {
        isVisible: boolean;
        content: string;
    };
    content: string;
};

/**
 * Specification of data available within a single block of a course.
 *
 * This data varies widely between the different kinds of blocks, so therefore
 * we've created extensions of this base class for each of the different
 * available block types.
 */
export abstract class BlockModel extends ObservableModel {
    /**
     * Internal id, which is created by the [[CourseModel]] whenever a block is
     * being added to a course based on an auto-incremented value.
     */
    public readonly id: string;

    /**
     * Contains the title of this block (this is entered by the teacher himself,
     * as he is creating the course).
     */
    public title: string;

    /**
     * Contains the teacher note entered for this block (this can be enabled /
     * disabled by the teacher in the viewer).
     */
    public note: {
        /**
         * Contains the string content entered by the user.
         */
        content: string;

        /**
         * Specifies if the teacher has enabled this note in the frontend.
         */
        enabled: boolean;
    };

    /**
     * Apply initial data when the model is instantiated.
     */
    constructor(initial: BlockData) {
        super();

        this.id = initial.id;
        this.title = initial.title;
        this.note = initial.note;
    }

    /**
     * Helper that'll generate a snapshot of the generic block data, and then
     * combine it with the content snapshot implemented by the different
     * available kinds of data blocks.
     */
    public getSnapshot(): string {
        return JSON.stringify({
            title: this.title,
            note: this.note,
            content: this.getContentSnapshot(),
        });
    }

    /**
     * Helper that'll return a serialized version of the content, so that it can
     * be stored remotely.
     */
    public abstract serializeContent(): string;

    /**
     * A snapshot should uniquely represent all observable data of the model, so
     * that we can easily determine if changes have been made between two
     * snapshots.
     */
    protected abstract getContentSnapshot(): string;
}
